/**
 * The account's active brand tone — the string the post inspector's tone
 * control falls back to, and the thing "if they have tone already from
 * onboarding, that should be used throughout" actually means at this surface.
 *
 * 🔴 IDENTITY: `user_brand_voices` is ACCOUNT-scoped
 * (src/lib/identity/table-scope.generated.ts). Reading it with a raw
 * `user.id` returns ZERO rows for a member — no error, no empty state, the
 * brand option simply stops existing for everyone who is not the owner.
 * Verified against prod: under a simulated session as clayton
 * (`2642a9d9-…`, a member of justin `cc0a0d82-…`), RLS exposes exactly ONE
 * row — justin's — while clayton's own raw-id rows are invisible. So the only
 * correct read here is through `brandVoiceService`, whose
 * `ensureAuthenticatedOwner()` already resolves the account owner.
 *
 * The selection mirrors BOTH branches of `resolveActiveVoice` — the kit branch
 * and the no-kit branch. That is not defensive symmetry: 326 of justin's 357
 * prod posts carry `brand_kit_id = null`, so a kit-only lookup would be a
 * silent no-op on 91% of real posts and would look completely fine in review.
 */

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import type { BrandVoiceWithDefaults } from '@/services/data/BrandVoiceService';
import { brandVoiceService } from '@/services/data/BrandVoiceService';

export interface UseBrandToneResult {
  brandTone: string | null;
  isLoading: boolean;
}

type VoiceRow = BrandVoiceWithDefaults;

function byBoolDesc(a: boolean | null | undefined, b: boolean | null | undefined): number {
  return Number(Boolean(b)) - Number(Boolean(a));
}

function byUpdatedDesc(a: VoiceRow, b: VoiceRow): number {
  const left = a.updated_at ? Date.parse(a.updated_at) : 0;
  const right = b.updated_at ? Date.parse(b.updated_at) : 0;
  return right - left;
}

/**
 * Exported for unit testing without a Supabase client. Returns null rather
 * than guessing when several live voices are in play and none is marked
 * default — the same refusal `resolveActiveVoice` makes, and the reason a
 * wrong brand's voice cannot leak into a caption.
 */
export function selectActiveVoiceTone(voices: VoiceRow[], kitId: string | null): string | null {
  const live = voices.filter((voice) => !voice.archived_at);
  const scoped = kitId ? live.filter((voice) => voice.brand_kit_id === kitId) : live;
  if (scoped.length === 0) return null;

  const ordered = [...scoped].sort((a, b) => {
    const byDefault = byBoolDesc(a.is_default, b.is_default);
    if (byDefault !== 0) return byDefault;
    if (!kitId) {
      const byActive = byBoolDesc(a.is_active, b.is_active);
      if (byActive !== 0) return byActive;
    }
    return byUpdatedDesc(a, b);
  });

  const chosen = scoped.length === 1 || ordered[0].is_default ? ordered[0] : null;
  const tone = chosen?.tone?.trim();
  return tone ? tone : null;
}

export function useBrandTone(kitId: string | null): UseBrandToneResult {
  const { user } = useAuth();
  const query = useQuery<string | null, Error>({
    // The owner id is resolved inside the service, so the raw actor id is the
    // right cache discriminator here: two members of the same account must not
    // share a cache entry across an account switch in one SPA session.
    queryKey: ['brand-tone', user?.id ?? 'anon', kitId ?? 'no-kit'],
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await brandVoiceService.getBrandVoices();
      if (response.error) throw new Error(response.error.message);
      return selectActiveVoiceTone(response.data ?? [], kitId);
    },
  });

  return {
    brandTone: query.data ?? null,
    isLoading: query.isPending && Boolean(user?.id),
  };
}
